import type {
  ServiceGetAllResponse,
  ServiceGetByIdResponse,
  ServiceMutationOptionType,
  ServiceQueryOptionType
} from '@/types/service'

import { api, handleAxiosError } from '@/lib/axios'

import type {
  CekPadanResponse,
  DukcapilHealthResponse,
  KelengkapanDataItem,
  KomoditasPetaniDataType,
  PetaniDataType,
  UpdatePadanPayload
} from './types'
import type {
  AddComodityValues,
  CheckDataPetaniValues,
  FilterPetaniValues,
  MutationPetaniValues
} from './validation'

import { lahan } from './lahan'

const PETANI_QUERY_KEY = (key?: string | Record<string, any>) =>
  ['daftar-petani', key] as const

const getAll = (
  params?: FilterPetaniValues
): ServiceQueryOptionType<ServiceGetAllResponse<Array<PetaniDataType>>> => ({
  queryKey: PETANI_QUERY_KEY(params),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<PetaniDataType>>
      >('/kelembagaan/petani', {
        params: {
          ...params,
          isSearch: undefined
        }
      })
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllKomoditasPetani = (
  params?: FilterPetaniValues
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<KomoditasPetaniDataType>>
> => ({
  queryKey: PETANI_QUERY_KEY(params),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<KomoditasPetaniDataType>>
      >(`/kelembagaan/petani/${params?.id_petani}/komoditas`, {
        params
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getById = (
  id: string
): ServiceQueryOptionType<ServiceGetByIdResponse<PetaniDataType>> => ({
  queryKey: PETANI_QUERY_KEY(id),
  queryFn: async () => {
    try {
      const response = await api.get<ServiceGetByIdResponse<PetaniDataType>>(
        `/kelembagaan/petani/${id}`
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const checkDukcapilHealth =
  (): ServiceQueryOptionType<DukcapilHealthResponse> => ({
    queryKey: PETANI_QUERY_KEY('health-dukcapil'),
    queryFn: async () => {
      try {
        const response = await api.get<DukcapilHealthResponse>(
          '/health/dukcapil',
          { timeout: 30_000 }
        )
        return response.data
      } catch (error) {
        throw handleAxiosError(error)
      }
    },
    retry: false,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false
  })

const DUKCAPIL_REQUEST_TIMEOUT = 15_000

const throwRequestError = (error: unknown): never => {
  if (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    error.code === 'ECONNABORTED'
  ) {
    throw new Error('Waktu pengecekan habis. Silakan coba lagi.')
  }

  throw handleAxiosError(error)
}

const checkNik = (): ServiceMutationOptionType<
  PetaniDataType,
  Pick<CheckDataPetaniValues, 'nik'>
> => ({
  mutationFn: async (data: Pick<CheckDataPetaniValues, 'nik'>) => {
    try {
      const response = await api.post<PetaniDataType>(
        '/kelembagaan/petani/cek-nik',
        { nik: data.nik },
        { timeout: DUKCAPIL_REQUEST_TIMEOUT }
      )
      return response.data
    } catch (error) {
      throwRequestError(error)
    }
  }
})

const checkDataPetani = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<CheckDataPetaniValues>,
  CheckDataPetaniValues
> => ({
  mutationFn: async (data: CheckDataPetaniValues) => {
    try {
      const response = await api.post<
        ServiceGetByIdResponse<CheckDataPetaniValues>
      >(
        '/kelembagaan/petani/cek-dukcapil',
        { ...data, isValidNik: undefined },
        { timeout: DUKCAPIL_REQUEST_TIMEOUT }
      )
      return response.data
    } catch (error) {
      throwRequestError(error)
    }
  }
})

const create = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PetaniDataType>,
  MutationPetaniValues
> => ({
  mutationFn: async (data: MutationPetaniValues) => {
    try {
      const response = await api.post<ServiceGetByIdResponse<PetaniDataType>>(
        '/kelembagaan/petani',
        {
          ...data,
          petani_id: undefined,
          id_desa: data.id_desa.value,
          id_poktan: data.id_poktan?.map(poktan => poktan.value) ?? [],
          luas_lahan_diusahakan: data.luas_lahan_diusahakan
            ? Number.parseFloat(data.luas_lahan_diusahakan)
            : undefined,
          luas_lahan_dimiliki: data.luas_lahan_dimiliki
            ? Number.parseFloat(data.luas_lahan_dimiliki)
            : undefined
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const createKomoditasPetani = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<KomoditasPetaniDataType>,
  AddComodityValues
> => ({
  mutationFn: async (data: AddComodityValues) => {
    try {
      const response = await api.post<
        ServiceGetByIdResponse<KomoditasPetaniDataType>
      >(`/kelembagaan/petani/${data.petani_id}/komoditas`, {
        ...data,
        komoditas: data.komoditas.map(komoditas => ({
          id: komoditas.id?.value || '',
          volume: komoditas.volume ? Number.parseFloat(komoditas.volume) : 0
        }))
      })
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const update = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PetaniDataType>,
  MutationPetaniValues
> => ({
  mutationFn: async (data: MutationPetaniValues) => {
    try {
      const response = await api.put<ServiceGetByIdResponse<PetaniDataType>>(
        `/kelembagaan/petani/${data.petani_id}`,
        {
          ...data,
          id_desa: data.id_desa.value,
          id_poktan: data.id_poktan?.map(poktan => poktan.value) ?? [],
          luas_lahan_diusahakan: data.luas_lahan_diusahakan
            ? Number.parseFloat(data.luas_lahan_diusahakan)
            : undefined,
          luas_lahan_dimiliki: data.luas_lahan_dimiliki
            ? Number.parseFloat(data.luas_lahan_dimiliki)
            : undefined
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const updateKomoditasPetani = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<KomoditasPetaniDataType>,
  AddComodityValues
> => ({
  mutationFn: async (data: AddComodityValues) => {
    try {
      const response = await api.put<
        ServiceGetByIdResponse<KomoditasPetaniDataType>
      >(
        `/kelembagaan/petani/${data?.petani_id}/komoditas/update/${data?.komoditas_id}`,
        {
          id_komoditas: data.komoditas?.[0]?.id?.value || '',
          volume: data.komoditas?.[0]?.volume
            ? Number.parseFloat(data.komoditas?.[0]?.volume)
            : 0
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const deleteData = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PetaniDataType>,
  { petani_id: string }
> => ({
  mutationFn: async (data: { petani_id: string }) => {
    try {
      const response = await api.delete<ServiceGetByIdResponse<PetaniDataType>>(
        `/kelembagaan/petani/${data.petani_id}`
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const deleteKomoditasPetani = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<KomoditasPetaniDataType>,
  { id_komoditas: string; id_petani: string }
> => ({
  mutationFn: async (data: { id_komoditas: string; id_petani: string }) => {
    try {
      const response = await api.delete<
        ServiceGetByIdResponse<KomoditasPetaniDataType>
      >(
        `/kelembagaan/petani/${data?.id_petani}/komoditas/${data?.id_komoditas}`
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getStatusLogs = (
  id: string
): ServiceQueryOptionType<ServiceGetAllResponse<Array<any>>> => ({
  queryKey: PETANI_QUERY_KEY(`status-logs-${id}`),
  queryFn: async () => {
    try {
      const response = await api.get<ServiceGetAllResponse<Array<any>>>(
        `/kelembagaan/petani/${id}/status-logs`
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getKelengkapanData = (
  id: string
): ServiceQueryOptionType<ServiceGetAllResponse<Array<KelengkapanDataItem>>> => ({
  queryKey: PETANI_QUERY_KEY(`kelengkapan-data-${id}`),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<KelengkapanDataItem>>
      >(`/kelembagaan/petani/${id}/kelengkapan-data`)
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const pindahDomisili = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<any>,
  {
    petani_id: string
    provinsi_baru: string
    kota_baru: string
    kecamatan_baru: string
    desa_baru: string
    alamat_baru: string
    alasan: string
    tanggal: string
    lampiran?: File
  }
> => ({
  mutationFn: async data => {
    try {
      const formData = new FormData()
      formData.append('provinsi_baru', data.provinsi_baru)
      formData.append('kota_baru', data.kota_baru)
      formData.append('kecamatan_baru', data.kecamatan_baru)
      formData.append('desa_baru', data.desa_baru)
      formData.append('alamat_baru', data.alamat_baru)
      formData.append('alasan', data.alasan)
      formData.append('tanggal', data.tanggal)
      if (data.lampiran) {
        formData.append('lampiran', data.lampiran)
      }

      const response = await api.post<ServiceGetByIdResponse<any>>(
        `/kelembagaan/petani/${data.petani_id}/domisili-logs`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getDomisiliLogs = (
  petaniId: string
): ServiceQueryOptionType<ServiceGetAllResponse<any>> => ({
  queryKey: PETANI_QUERY_KEY(['domisili-logs', petaniId]),
  queryFn: async () => {
    try {
      const response = await api.get<ServiceGetAllResponse<any>>(
        `/kelembagaan/petani/${petaniId}/domisili-logs`,
        {
          params: {
            include:
              'provinsi_lama,kota_lama,kecamatan_lama,desa_lama,provinsi_baru,kota_baru,kecamatan_baru,desa_baru'
          }
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const pindahStatus = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<any>,
  {
    petani_id: string
    id_status_baru: string
    tanggal: string
    alasan?: string
    lampiran?: File
  }
> => ({
  mutationFn: async data => {
    try {
      const formData = new FormData()
      formData.append('id_status_baru', data.id_status_baru)
      formData.append('tanggal', data.tanggal)
      if (data.alasan) {
        formData.append('alasan', data.alasan)
      }
      if (data.lampiran) {
        formData.append('lampiran', data.lampiran)
      }

      const response = await api.post<ServiceGetByIdResponse<any>>(
        `/kelembagaan/petani/${data.petani_id}/status-logs`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const cekPadan = (): ServiceMutationOptionType<
  CekPadanResponse,
  { petani_id: string }
> => ({
  mutationFn: async (data: { petani_id: string }) => {
    try {
      const response = await api.post<CekPadanResponse>(
        `/kelembagaan/petani/${data.petani_id}/cek-padan`,
        undefined,
        { timeout: DUKCAPIL_REQUEST_TIMEOUT }
      )
      return response.data
    } catch (error) {
      throwRequestError(error)
    }
  }
})

const updatePadan = (): ServiceMutationOptionType<
  CekPadanResponse,
  UpdatePadanPayload
> => ({
  mutationFn: async (data: UpdatePadanPayload) => {
    try {
      const response = await api.put<CekPadanResponse>(
        `/kelembagaan/petani/${data.petani_id}/cek-padan`,
        {
          nik: data.nik,
          nama_lengkap: data.nama_lengkap,
          tempat_lahir: data.tempat_lahir,
          tanggal_lahir: data.tanggal_lahir,
          jenis_kelamin: data.jenis_kelamin
        },
        { timeout: DUKCAPIL_REQUEST_TIMEOUT }
      )
      return response.data
    } catch (error) {
      throwRequestError(error)
    }
  }
})

const exportData = (params?: FilterPetaniValues) => ({
  mutationFn: async () => {
    try {
      const response = await api.get<{
        status: string
        url?: string | null
        job_id?: string
        message?: string
        progress?: number
      }>(`/kelembagaan/petani/export`, {
        params
      })
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

export const daftarPetani = {
  queryKey: [PETANI_QUERY_KEY(undefined)[0]],
  getAll,
  checkDataPetani,
  checkDukcapilHealth,
  checkNik,
  create,
  getById,
  getAllKomoditasPetani,
  getStatusLogs,
  getKelengkapanData,
  getDomisiliLogs,
  update,
  deleteData,
  deleteKomoditasPetani,
  createKomoditasPetani,
  updateKomoditasPetani,
  pindahDomisili,
  pindahStatus,
  cekPadan,
  updatePadan,
  exportData,
  lahan
}
